// src/pages/events/EventDetail.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box, Typography, Paper, Stack, Chip, Button, Avatar,
  CircularProgress, Alert, IconButton, Divider, AvatarGroup,
  useMediaQuery, useTheme, alpha,
} from "@mui/material";
import {
  AccessTime, EventNote, LocationOn, Person, People,
  ArrowBack, LiveTv, OpenInNew, CheckCircle, Cancel,
  Edit as EditIcon, Block, ContentCopy, Share, Tag,
} from "@mui/icons-material";
import axios from "axios";
import TopBar from "../globalComponents/topbar";
import Footer from "../globalComponents/footer";

const API = "https://backend-global-1vq9.onrender.com";

const C = {
  primary: "#0A2E8A", primaryDark: "#061E5C", primaryLight: "#3B5BC9",
  primarySoft: "#E8EDFC", primarySofter: "#F2F5FE",
  secondary: "#10682A", secondaryDark: "#0A4A1C", secondaryLight: "#2E9148",
  secondarySoft: "#E3F2E8",
  bg: "#FAFBFC", paper: "#FFFFFF",
  border: "#E5E9F0", borderSoft: "#EEF1F6",
  textPrimary: "#0D1117", textSecondary: "#5B6B7E", textMuted: "#8A9BB0",
  warning: "#D97706", warningSoft: "#FEF3C7",
  error: "#DC2626", errorSoft: "#FEE2E2",
  live: "#DC2626",
};

const PLATFORM_LABELS = {
  "zoom": "Zoom", "google-meet": "Google Meet", "youtube-live": "YouTube Live",
  "microsoft-teams": "Microsoft Teams", "in-person": "In Person", "other": "Other",
};

const PLATFORM_COLORS = {
  "zoom": "#2D8CFF", "google-meet": "#00897B", "youtube-live": "#FF0000",
  "microsoft-teams": "#5059C9", "in-person": "#10682A", "other": "#6B7280",
};

const resolveImageUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return `${API}/${path.replace(/\\/g, "/").replace(/^\//, "")}`;
};

const formatFullDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    weekday: "long", month: "long", day: "numeric", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

const formatTimeRange = (start, end) => {
  const s = new Date(start);
  const e = new Date(end);
  const fmt = (d) => d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  return `${fmt(s)} – ${fmt(e)}`;
};

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [feedback, setFeedback] = useState({ type: "", text: "" });

  const user = (() => {
    try { return JSON.parse(localStorage.getItem("user")); }
    catch { return null; }
  })();
  const userId = user?.id;
  const userRole = (user?.role || "").toLowerCase();
  const normalizedRole = userRole === "learner" ? "mentee" : userRole;

  const isRegistered = event?.attendees?.some(
    (a) => String(a.userId) === String(userId)
  );
  const isHost = event && String(event.hostId) === String(userId);
  const isSuperuser = userRole === "superuser";
  const canEdit = isHost || isSuperuser;

  const liveStatus = event?.computedStatus || "upcoming";
  const isLive = liveStatus === "live";
  const isPast = liveStatus === "ended";
  const isCancelled = event?.status === "cancelled";
  const isFull = event?.isFull || (event?.capacity > 0 && event?.attendees?.length >= event?.capacity);

  // ── Fetch event ──
  const fetchEvent = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/events/${id}`);
      setEvent(res.data?.data);
    } catch (err) {
      setError(err.response?.data?.msg || "Failed to load event");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchEvent(); }, [fetchEvent]);

  // Auto-refresh every 30s if event is starting soon or live (so the "Join" button activates)
  useEffect(() => {
    if (!event) return;
    const start = new Date(event.startTime).getTime();
    const end   = new Date(event.endTime).getTime();
    const now = Date.now();
    const startsWithinHour = start > now && (start - now) < 60 * 60 * 1000;
    if (isLive || startsWithinHour) {
      const interval = setInterval(fetchEvent, 30_000);
      return () => clearInterval(interval);
    }
  }, [event, isLive, fetchEvent]);

  // ── Actions ──
  const handleRegister = async () => {
    if (!user) {
      setFeedback({ type: "error", text: "Please sign in to register." });
      return;
    }
    try {
      setActionLoading(true);
      const res = await axios.post(`${API}/api/events/${id}/register`, {
        userId,
        name: user.fullName,
        email: user.email,
        role: normalizedRole,
      });
      setEvent(res.data?.data);
      setFeedback({ type: "success", text: "You're registered! See you there." });
    } catch (err) {
      setFeedback({ type: "error", text: err.response?.data?.msg || "Failed to register" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnregister = async () => {
    if (!window.confirm("Cancel your registration for this event?")) return;
    try {
      setActionLoading(true);
      const res = await axios.post(`${API}/api/events/${id}/unregister`, { userId });
      setEvent(res.data?.data);
      setFeedback({ type: "success", text: "You've unregistered from this event." });
    } catch (err) {
      setFeedback({ type: "error", text: err.response?.data?.msg || "Failed to unregister" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelEvent = async () => {
    if (!window.confirm("Cancel this event? Attendees won't be able to join.")) return;
    try {
      setActionLoading(true);
      const res = await axios.post(`${API}/api/events/${id}/cancel`, {
        requesterId: userId, requesterRole: userRole,
      });
      setEvent(res.data?.data);
      setFeedback({ type: "success", text: "Event cancelled." });
    } catch (err) {
      setFeedback({ type: "error", text: err.response?.data?.msg || "Failed to cancel" });
    } finally {
      setActionLoading(false);
    }
  };

  const handleCopyLink = () => {
    const url = window.location.href;
    navigator.clipboard?.writeText(url);
    setFeedback({ type: "success", text: "Event link copied to clipboard." });
  };

  const handleJoin = () => {
    if (event?.meetingUrl) {
      window.open(event.meetingUrl, "_blank", "noopener,noreferrer");
    }
  };

  // ── Render guards ──
  if (loading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: C.bg }}>
        <TopBar />
        <Box flex={1} display="flex" alignItems="center" justifyContent="center">
          <CircularProgress sx={{ color: C.primary }} />
        </Box>
        <Footer />
      </Box>
    );
  }

  if (error || !event) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: C.bg }}>
        <TopBar />
        <Box flex={1} display="flex" alignItems="center" justifyContent="center" px={3}>
          <Paper elevation={0} sx={{
            p: 4, textAlign: "center", borderRadius: 3, maxWidth: 480,
            border: `1px solid ${C.border}`,
          }}>
            <Typography sx={{ fontWeight: 800, color: C.textPrimary, mb: 1 }}>
              Event not found
            </Typography>
            <Typography sx={{ color: C.textSecondary, fontSize: 13, mb: 3 }}>
              {error || "This event may have been removed or is unavailable."}
            </Typography>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate("/events")}
              variant="contained"
              sx={{
                bgcolor: C.primary, textTransform: "none", fontWeight: 700,
                "&:hover": { bgcolor: C.primaryDark },
              }}
            >
              Back to events
            </Button>
          </Paper>
        </Box>
        <Footer />
      </Box>
    );
  }

  const platformColor = PLATFORM_COLORS[event.platform] || C.textSecondary;
  const canShowEmbed = event.platform === "youtube-live" && event.youtubeVideoId && isLive;

  // ── Action button copy ──
  let actionLabel, actionDisabled, actionOnClick, actionBg;
  if (isCancelled) {
    actionLabel = "Cancelled"; actionDisabled = true; actionBg = C.error;
  } else if (isPast) {
    actionLabel = "Event ended"; actionDisabled = true; actionBg = C.textMuted;
  } else if (isRegistered && isLive) {
    actionLabel = "Join Now";   actionOnClick = handleJoin; actionBg = C.live;
  } else if (isRegistered) {
    actionLabel = "Unregister"; actionOnClick = handleUnregister; actionBg = C.error;
  } else if (isFull) {
    actionLabel = "Event Full"; actionDisabled = true; actionBg = C.textMuted;
  } else {
    actionLabel = "Register";   actionOnClick = handleRegister; actionBg = C.primary;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh", bgcolor: C.bg }}>
      <TopBar />

      <Box sx={{ flex: 1, px: { xs: 2, md: 4 }, py: 3, maxWidth: 1100, mx: "auto", width: "100%" }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate("/events")}
          sx={{
            color: C.textSecondary, textTransform: "none",
            fontWeight: 700, fontSize: 13, mb: 2,
            "&:hover": { bgcolor: C.primarySofter, color: C.primary },
          }}
        >
          Back to events
        </Button>

        {feedback.text && (
          <Alert
            severity={feedback.type}
            onClose={() => setFeedback({ type: "", text: "" })}
            sx={{ mb: 2, borderRadius: 2.5, fontSize: 13 }}
          >
            {feedback.text}
          </Alert>
        )}

        {/* ── Hero / cover ──────────────────────────────── */}
        <Paper
          elevation={0}
          sx={{
            position: "relative",
            borderRadius: 3, overflow: "hidden", mb: 3,
            border: `1px solid ${C.border}`,
            bgcolor: C.paper,
            boxShadow: "0 4px 16px rgba(13,17,23,0.06)",
          }}
        >
          <Box sx={{
            height: { xs: 200, sm: 280 },
            position: "relative",
            background: event.coverImage
              ? `linear-gradient(to bottom, transparent 50%, rgba(0,0,0,0.5)), url(${resolveImageUrl(event.coverImage)})`
              : `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}>
            {!event.coverImage && (
              <Box sx={{
                position: "absolute",
                top: "-30%", right: "-10%",
                width: 300, height: 300, borderRadius: "50%",
                background: `radial-gradient(circle, ${C.secondary}40 0%, transparent 70%)`,
              }} />
            )}

            {isLive && (
              <Box sx={{
                position: "absolute", top: 16, left: 16,
                bgcolor: C.live, color: "#fff",
                fontSize: 11, fontWeight: 800, letterSpacing: 0.8,
                px: 1.25, py: 0.5, borderRadius: 1,
                display: "flex", alignItems: "center", gap: 0.75,
                boxShadow: `0 4px 12px ${C.live}50`,
                animation: "pulse 2s ease-in-out infinite",
                "@keyframes pulse": {
                  "0%, 100%": { transform: "scale(1)" },
                  "50%": { transform: "scale(1.04)" },
                },
              }}>
                <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "#fff" }} />
                LIVE NOW
              </Box>
            )}

            <Chip
              label={PLATFORM_LABELS[event.platform] || "Event"}
              size="small"
              sx={{
                position: "absolute", top: 16, right: 16,
                bgcolor: platformColor, color: "#fff",
                fontWeight: 800, fontSize: 11, height: 24,
                border: "1px solid rgba(255,255,255,0.3)",
              }}
            />
          </Box>

          {/* Title block (overflows under image) */}
          <Box sx={{ p: { xs: 2.5, sm: 3 } }}>
            {event.category && event.category !== "general" && (
              <Typography sx={{
                fontSize: 11, fontWeight: 800, letterSpacing: 1.2,
                textTransform: "uppercase", color: C.primary, mb: 1,
              }}>
                {event.category}
              </Typography>
            )}
            <Typography sx={{
              fontWeight: 800, fontSize: { xs: 22, sm: 28 },
              color: C.textPrimary, lineHeight: 1.2, mb: 1.5, letterSpacing: -0.4,
            }}>
              {event.title}
            </Typography>

            <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap mb={1}>
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <AccessTime sx={{ fontSize: 16, color: C.textSecondary }} />
                <Typography sx={{ fontSize: 13, color: C.textSecondary, fontWeight: 600 }}>
                  {formatFullDateTime(event.startTime)}
                </Typography>
              </Stack>
              <Box sx={{ width: 4, height: 4, borderRadius: "50%", bgcolor: C.textMuted }} />
              <Typography sx={{ fontSize: 13, color: C.textSecondary, fontWeight: 600 }}>
                {formatTimeRange(event.startTime, event.endTime)}
              </Typography>
              {event.timezone && (
                <>
                  <Box sx={{ width: 4, height: 4, borderRadius: "50%", bgcolor: C.textMuted }} />
                  <Typography sx={{ fontSize: 12, color: C.textMuted, fontWeight: 600 }}>
                    {event.timezone}
                  </Typography>
                </>
              )}
            </Stack>

            {/* Action row */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.25}
              alignItems={{ xs: "stretch", sm: "center" }}
              mt={2.5}
            >
              <Button
                variant="contained"
                onClick={actionOnClick}
                disabled={actionDisabled || actionLoading}
                size="large"
                startIcon={
                  actionLoading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> :
                  isRegistered && isLive ? <LiveTv /> :
                  isRegistered ? <Cancel /> :
                  <CheckCircle />
                }
                sx={{
                  bgcolor: actionBg, color: "#fff",
                  textTransform: "none", fontWeight: 800, fontSize: 14,
                  px: 3, py: 1.25, borderRadius: 2.5,
                  boxShadow: actionDisabled ? "none" : `0 4px 14px ${actionBg}40`,
                  "&:hover": { bgcolor: actionBg, opacity: 0.92 },
                  "&.Mui-disabled": { bgcolor: actionBg, opacity: 0.6, color: "#fff" },
                }}
              >
                {actionLabel}
              </Button>

              <Button
                onClick={handleCopyLink}
                startIcon={<Share sx={{ fontSize: 16 }} />}
                sx={{
                  textTransform: "none", fontWeight: 700, fontSize: 13,
                  color: C.textSecondary, borderRadius: 2.5, px: 2,
                  border: `1px solid ${C.border}`,
                  "&:hover": { bgcolor: C.primarySofter, color: C.primary, borderColor: C.primary },
                }}
              >
                Share
              </Button>

              {canEdit && !isCancelled && (
                <>
                  <Button
                    onClick={() => navigate(`/events/${event._id}/edit`)}
                    startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      textTransform: "none", fontWeight: 700, fontSize: 13,
                      color: C.primary, borderRadius: 2.5, px: 2,
                      border: `1px solid ${C.primarySoft}`, bgcolor: C.primarySofter,
                      "&:hover": { bgcolor: C.primarySoft },
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    onClick={handleCancelEvent}
                    startIcon={<Block sx={{ fontSize: 16 }} />}
                    disabled={actionLoading}
                    sx={{
                      textTransform: "none", fontWeight: 700, fontSize: 13,
                      color: C.error, borderRadius: 2.5, px: 2,
                      border: `1px solid ${C.errorSoft}`, bgcolor: C.errorSoft,
                      "&:hover": { bgcolor: alpha(C.error, 0.15) },
                    }}
                  >
                    Cancel Event
                  </Button>
                </>
              )}
            </Stack>
          </Box>
        </Paper>

        {/* ── Two-column layout: details (left) + side card (right) ──── */}
        <Box sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 320px" },
          gap: 3,
        }}>
          <Box>
            {/* YouTube embed (only when live + YT platform) */}
            {canShowEmbed && (
              <Paper elevation={0} sx={{
                position: "relative", paddingTop: "56.25%", // 16:9
                borderRadius: 3, overflow: "hidden", mb: 2.5,
                border: `1px solid ${C.border}`,
                boxShadow: "0 4px 16px rgba(13,17,23,0.06)",
              }}>
                <Box
                  component="iframe"
                  src={`https://www.youtube.com/embed/${event.youtubeVideoId}?autoplay=1`}
                  title={event.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  sx={{
                    position: "absolute", inset: 0,
                    width: "100%", height: "100%",
                  }}
                />
              </Paper>
            )}

            {/* Description */}
            <Paper elevation={0} sx={{
              p: { xs: 2.5, sm: 3 }, borderRadius: 3,
              border: `1px solid ${C.border}`, bgcolor: C.paper, mb: 2.5,
              boxShadow: "0 2px 12px rgba(13,17,23,0.04)",
            }}>
              <Typography sx={{
                fontWeight: 800, fontSize: 15, color: C.textPrimary, mb: 1.5,
                letterSpacing: -0.2,
              }}>
                About this event
              </Typography>
              <Typography sx={{
                fontSize: 14, color: C.textSecondary, lineHeight: 1.7,
                whiteSpace: "pre-wrap",
              }}>
                {event.description}
              </Typography>

              {event.tags?.length > 0 && (
                <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.75} mt={2.5}>
                  {event.tags.map((tag, i) => (
                    <Chip
                      key={i}
                      icon={<Tag sx={{ fontSize: "12px !important" }} />}
                      label={tag}
                      size="small"
                      sx={{
                        bgcolor: C.primarySoft, color: C.primary,
                        fontWeight: 600, fontSize: 11, height: 22,
                      }}
                    />
                  ))}
                </Stack>
              )}
            </Paper>

            {/* In-person address */}
            {event.platform === "in-person" && event.venueAddress && (
              <Paper elevation={0} sx={{
                p: { xs: 2.5, sm: 3 }, borderRadius: 3,
                border: `1px solid ${C.border}`, bgcolor: C.paper,
                boxShadow: "0 2px 12px rgba(13,17,23,0.04)",
              }}>
                <Stack direction="row" spacing={1.25} alignItems="flex-start">
                  <Box sx={{
                    width: 36, height: 36, borderRadius: 1.5,
                    background: `linear-gradient(135deg, ${C.secondary}, ${C.secondaryLight})`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                  }}>
                    <LocationOn sx={{ fontSize: 20, color: "#fff" }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 800, fontSize: 14, color: C.textPrimary, mb: 0.5 }}>
                      Venue
                    </Typography>
                    <Typography sx={{ fontSize: 13.5, color: C.textSecondary, lineHeight: 1.6 }}>
                      {event.venueAddress}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            )}
          </Box>

          {/* Side card */}
          <Box>
            <Paper elevation={0} sx={{
              p: 2.5, borderRadius: 3,
              border: `1px solid ${C.border}`, bgcolor: C.paper, mb: 2.5,
              boxShadow: "0 2px 12px rgba(13,17,23,0.04)",
              position: { md: "sticky" }, top: { md: 24 },
            }}>
              {/* Host */}
              <Typography sx={{
                fontSize: 11, fontWeight: 800, letterSpacing: 1,
                textTransform: "uppercase", color: C.textMuted, mb: 1,
              }}>
                Host
              </Typography>
              <Stack direction="row" spacing={1.5} alignItems="center" mb={2.5}>
                <Avatar sx={{
                  width: 42, height: 42,
                  background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`,
                  color: "#fff", fontWeight: 800, fontSize: 16,
                }}>
                  {(event.hostName || "?").charAt(0).toUpperCase()}
                </Avatar>
                <Box minWidth={0}>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: C.textPrimary }} noWrap>
                    {event.hostName || "Host"}
                  </Typography>
                  <Chip
                    label={event.hostRole}
                    size="small"
                    sx={{
                      bgcolor: C.secondarySoft, color: C.secondary,
                      fontWeight: 700, fontSize: 10, height: 18, mt: 0.4,
                      textTransform: "capitalize",
                    }}
                  />
                </Box>
              </Stack>

              <Divider sx={{ borderColor: C.borderSoft, mb: 2 }} />

              {/* Platform */}
              <Typography sx={{
                fontSize: 11, fontWeight: 800, letterSpacing: 1,
                textTransform: "uppercase", color: C.textMuted, mb: 1,
              }}>
                Platform
              </Typography>
              <Box sx={{
                p: 1.5, borderRadius: 2,
                bgcolor: alpha(platformColor, 0.08),
                border: `1px solid ${alpha(platformColor, 0.2)}`,
                mb: 2.5,
              }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box sx={{
                    width: 30, height: 30, borderRadius: 1,
                    bgcolor: platformColor,
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <LiveTv sx={{ fontSize: 16, color: "#fff" }} />
                  </Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.textPrimary }}>
                    {PLATFORM_LABELS[event.platform]}
                  </Typography>
                </Stack>
              </Box>

              {/* Attendees */}
              <Typography sx={{
                fontSize: 11, fontWeight: 800, letterSpacing: 1,
                textTransform: "uppercase", color: C.textMuted, mb: 1,
              }}>
                Registered ({event.attendees?.length || 0}{event.capacity > 0 ? ` / ${event.capacity}` : ""})
              </Typography>
              {event.attendees?.length > 0 ? (
                <AvatarGroup
                  max={6}
                  sx={{
                    justifyContent: "flex-start",
                    "& .MuiAvatar-root": {
                      width: 30, height: 30, fontSize: 12, fontWeight: 700,
                      border: `2px solid ${C.paper}`,
                    },
                  }}
                >
                  {event.attendees.map((a, i) => (
                    <Avatar key={i} sx={{
                      background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`,
                      color: "#fff",
                    }}>
                      {(a.name || "?").charAt(0).toUpperCase()}
                    </Avatar>
                  ))}
                </AvatarGroup>
              ) : (
                <Typography sx={{ fontSize: 12, color: C.textMuted }}>
                  Be the first to register.
                </Typography>
              )}
            </Paper>
          </Box>
        </Box>
      </Box>

      <Footer />
    </Box>
  );
}